import { supabase } from './supabase';
import { getCurrentHospitalId } from './hospitalContext';

export interface SubscriptionPlan {
  id: string;
  name: string;
  duration_months: number;
  price: number;
  currency: string;
  features: Record<string, any>;
  is_active: boolean;
}

export interface PaymentHistory {
  id: string;
  hospital_id: string;
  subscription_plan_id: string;
  amount: number; // Total amount (base + tax) for backward compatibility
  base_amount?: number | null; // Base amount (tax-exclusive)
  tax_amount?: number | null; // Tax amount
  tax_rate?: number | null; // Tax rate percentage
  currency: string;
  payment_method: string;
  transaction_id: string | null;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  subscription_period_months: number;
  payment_date: string;
  notes: string | null;
  created_at: string;
}

export interface HospitalSubscription {
  id: string;
  name: string;
  subscription_status: 'trial' | 'active' | 'expired' | 'suspended' | 'cancelled';
  subscription_expires_at: string | null;
  trial_started_at: string | null;
  trial_days: number;
  subscription_plan_id: string | null;
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  last_payment_date: string | null;
  next_payment_date: string | null;
}

/**
 * Check if hospital has active subscription
 */
export async function checkSubscriptionStatus(hospitalId?: string): Promise<boolean> {
  try {
    const currentHospitalId = hospitalId || await getCurrentHospitalId();

    if (!currentHospitalId) {
      console.warn('Subscription check: no hospital_id on user profile');
      return false;
    }

    // Prefer RPC: same hospital subscription for admin, staff, and doctors
    const { data: rpcActive, error: rpcError } = await supabase.rpc(
      'is_hospital_subscription_active',
      { p_hospital_id: currentHospitalId }
    );

    if (!rpcError && typeof rpcActive === 'boolean') {
      return rpcActive;
    }

    if (rpcError) {
      console.warn('RPC subscription check unavailable, using direct query:', rpcError.message);
    }

    const { data, error } = await supabase
      .from('hospitals')
      .select('subscription_status, subscription_expires_at, trial_started_at, trial_days')
      .eq('id', currentHospitalId)
      .single();

    if (error || !data) {
      console.error('Error checking subscription:', error);
      return false;
    }

    if (data.subscription_status === 'active') {
      if (data.subscription_expires_at) {
        const expiresAt = new Date(data.subscription_expires_at);
        return expiresAt > new Date();
      }
    }

    if (data.subscription_status === 'trial' && data.trial_started_at && data.trial_days) {
      const trialStart = new Date(data.trial_started_at);
      const daysSinceStart = Math.floor(
        (Date.now() - trialStart.getTime()) / (1000 * 60 * 60 * 24)
      );
      return daysSinceStart <= data.trial_days;
    }

    return false;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
}

/**
 * Get hospital subscription details
 */
export async function getHospitalSubscription(): Promise<HospitalSubscription | null> {
  try {
    const currentHospitalId = await getCurrentHospitalId();
    
    if (!currentHospitalId) {
      return null;
    }

    const { data, error } = await supabase
      .from('hospitals')
      .select('*')
      .eq('id', currentHospitalId)
      .single();

    if (error || !data) {
      console.error('Error fetching hospital subscription:', error);
      return null;
    }

    return data as HospitalSubscription;
  } catch (error) {
    console.error('Error getting hospital subscription:', error);
    return null;
  }
}

/**
 * Get all available subscription plans
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('duration_months', { ascending: true });

    if (error) {
      console.error('Error fetching subscription plans:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      return [];
    }

    console.log('Fetched subscription plans:', data?.length || 0, 'plans');
    if (data && data.length > 0) {
      console.log('Plans data:', data);
    } else {
      console.warn('No active subscription plans found in database');
    }

    return data || [];
  } catch (error) {
    console.error('Error getting subscription plans:', error);
    return [];
  }
}

/**
 * Activate subscription (demo/testing mode)
 */
export async function activateSubscriptionDemo(planId: string): Promise<boolean> {
  try {
    const currentHospitalId = await getCurrentHospitalId();
    
    if (!currentHospitalId) {
      throw new Error('No hospital context');
    }

    const { data, error } = await supabase.rpc('activate_subscription_demo', {
      p_hospital_id: currentHospitalId,
      p_plan_id: planId,
      p_payment_method: 'demo'
    });

    if (error) {
      console.error('Error activating subscription:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in activate subscription:', error);
    return false;
  }
}

/**
 * Get payment history for hospital
 */
export async function getPaymentHistory(): Promise<PaymentHistory[]> {
  try {
    const currentHospitalId = await getCurrentHospitalId();
    
    if (!currentHospitalId) {
      return [];
    }

    const { data, error } = await supabase
      .from('payment_history')
      .select('*')
      .eq('hospital_id', currentHospitalId)
      .order('payment_date', { ascending: false });

    if (error) {
      console.error('Error fetching payment history:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error getting payment history:', error);
    return [];
  }
}

/**
 * Get days remaining in trial or subscription
 */
export function getDaysRemaining(subscription: HospitalSubscription | null): {
  daysRemaining: number;
  isTrial: boolean;
  isExpired: boolean;
} {
  if (!subscription) {
    return { daysRemaining: 0, isTrial: false, isExpired: true };
  }

  let expiryDate: Date | null = null;
  let isTrial = false;

  // Check if it's an active subscription
  if (subscription.subscription_status === 'active' && subscription.subscription_expires_at) {
    expiryDate = new Date(subscription.subscription_expires_at);
    isTrial = false;
  } 
  // Check if it's a trial
  else if (subscription.subscription_status === 'trial' && subscription.trial_started_at) {
    const trialStart = new Date(subscription.trial_started_at);
    expiryDate = new Date(trialStart.getTime() + subscription.trial_days * 24 * 60 * 60 * 1000);
    isTrial = true;
  }

  if (!expiryDate) {
    return { daysRemaining: 0, isTrial: false, isExpired: true };
  }

  const now = new Date();
  const diffTime = expiryDate.getTime() - now.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  const isExpired = daysRemaining <= 0;

  return { daysRemaining: Math.max(0, daysRemaining), isTrial, isExpired };
}

